import { Card, Divider } from 'antd';
import React, { FC } from 'react';
import {
	extractTextContent,
	flattenTreeData,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import {
	getTroubleshootingAnswerFormatted,
	STEP_INPUT_TYPE_MAP,
	subAnswerReduceFunction,
} from '../../utils/TroubleshootingUtils';
import { ProtectedImageThumbnails } from '../protected_image_thumbnails/ProtectedImageThumbnails';
import { KeyValueDisplay } from '../key_value_display/KeyValueDisplay';
import { connect } from 'react-redux';
import FileUploader from '../file_uploader/FileUploader';

export const TroubleshootingAnswerDisplay: FC<any> = ({
	answer,
	userType,
	title,
}): React.ReactElement => {
	const renderObject = (obj) => {
		switch (obj.type) {
			case STEP_INPUT_TYPE_MAP.IMAGE:
				return (
					<ProtectedImageThumbnails
						style={{ marginTop: 16 }}
						imageWidth={64}
						imageHeight={64}
						photoStrings={obj.value}
						role={userType}
					/>
				);
			case STEP_INPUT_TYPE_MAP.PDF:
			case STEP_INPUT_TYPE_MAP.ANY_FILE:
				return (
					<FileUploader
						roleType={userType}
						defaultFileList={obj.value}
						showUploadList={{ showPreviewIcon: true, showRemoveIcon: false }}
						uploaderType="readOnly"
					/>
				);
			case STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE:
				return nullSafeGetOrElse('value', obj, []).length > 1 ? (
					<div className="flex flex-col">
						{obj.value.map((view, idx) => {
							return (
								<div key={idx} className="flex flex-col gap-y-2">
									<span className="text-sm text-gray-400">{`Option ${idx + 1}`}</span>
									<span>{view}</span>
								</div>
							);
						})}
					</div>
				) : (
					nullSafeGetOrElse('value.0', obj, '--')
				);
			case STEP_INPUT_TYPE_MAP.GROUP:
				return nullSafeGetOrElse('value', obj, []).length > 0 ? (
					<div className="mb-4 grid w-full grid-cols-1 gap-x-28 gap-y-2 sm:grid-cols-2">
						{obj.value.map((view, idx) => {
							const { label, value } = view;
							return (
								<div key={idx} className="flex flex-col gap-y-2">
									<span className="text-sm text-gray-400">{label || `Field ${idx + 1}`}</span>
									<span className="text-base">{value}</span>
								</div>
							);
						})}
					</div>
				) : (
					'--'
				);
			case STEP_INPUT_TYPE_MAP.REPEATABLE_GROUP:
				return nullSafeGetOrElse('value', obj, []).length > 0 ? (
					<div className="flex flex-col">
						{obj.value.map((group, gIdx) => (
							<div key={`ts-ans-rgrp-${gIdx}`} className="mb-4">
								<div className="mb-2 text-sm text-gray-400">
									{title} ({gIdx + 1})
								</div>
								<div className="grid w-full grid-cols-1 gap-x-28 gap-y-2 sm:grid-cols-2">
									{group.map((view, idx) => {
										const { label, value } = view;
										return (
											<div key={idx} className="flex flex-col gap-y-2">
												<span className="text-sm text-gray-400">
													{' '}
													{label || `Field ${idx + 1}`}
												</span>
												<span className="text-base">{value}</span>
											</div>
										);
									})}
								</div>
							</div>
						))}
					</div>
				) : (
					'--'
				);
			default:
				return null;
		}
	};

	return typeof answer === 'object' ? renderObject(answer) : answer;
};

const EachTroubleshootingGroupDisplay: FC<any> = ({
	tsId,
	answers,
	userType,
}): React.ReactElement => {
	const answersWithSubAnswer = flattenTreeData(answers, subAnswerReduceFunction, 'subStepAnswers');

	return (
		<div className="rowSpacing">
			<Card title="Troubleshooting">
				<div className="flex flex-col">
					{answersWithSubAnswer.map((ans, idx) => (
						<div key={`${tsId}-each-ts-answer-${idx}`} className="mb-7">
							{nullSafeGet('troubleshootingStep.title', ans) ? (
								<div className="h5regular text-character-secondary">
									{extractTextContent(nullSafeGet('troubleshootingStep.title', ans))}
								</div>
							) : null}
							{nullSafeGet('troubleshootingStep.description', ans) ? (
								<div
									className="bodyregular mt-1 text-character-secondary [&_p:last-child]:mb-0 [&_p]:mb-1 [&_p]:mt-0"
									dangerouslySetInnerHTML={{
										__html: nullSafeGet('troubleshootingStep.description', ans),
									}}
								/>
							) : null}
							<div className="h5regular mt-2">
								{nullSafeGet('answer', ans) ||
								nullSafeGet('multipleAnswers.0', ans) ||
								nullSafeGet('attachments.0', ans) ||
								nullSafeGet('groupFieldAnswers.0', ans) ? (
									<TroubleshootingAnswerDisplay
										answer={getTroubleshootingAnswerFormatted(ans)}
										userType={userType}
										title={extractTextContent(nullSafeGet('troubleshootingStep.title', ans))}
									/>
								) : (
									'NA'
								)}
							</div>
						</div>
					))}
				</div>
			</Card>
		</div>
	);
};

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(EachTroubleshootingGroupDisplay);
